import React from 'react';
import { Typography } from '@mui/material';
import useTermsAndConditionsStyles from '../../../styles/settings/terms-and-conditions.style';
import { TitleWithPrint } from '../../title-with-print/title-with-print';

export default function TermsAndConditionsPage() {
    const classes = useTermsAndConditionsStyles();
    return (
        <div className={classes.root}>
            <TitleWithPrint
                variant="h3"
                titleClass={classes.pageTitle}
                title={'Sensei Wellness Services Terms of Service'}
                pdfUrl={`/static/pdfs/terms_of_service.pdf`}
            />
            <Typography variant="body1" paragraph className={classes.inc}>
                Sensei Wellness Holdings, Inc.
            </Typography>
            <Typography
                variant="body1"
                paragraph
                className={classes.lastUpdate}
            >
                Last updated: January 17, 2021
            </Typography>
            <Typography variant="body1" paragraph>
                Welcome, and thank you for your interest in Sensei Wellness
                Holdings, Inc. (“<b className={classes.boldText}>Sensei</b>,” “
                <b className={classes.boldText}>we</b>,” or “
                <b className={classes.boldText}>us</b>”), our website at
                www.sensei.com, and our wellness, nutrition, spa, and fitness
                services at retreats facilitated by Sensei (“
                <b className={classes.boldText}>Sensei Retreats</b>”) along with
                our related websites and member portals, networks, applications,
                mobile applications, including any personal wearable data
                monitoring devices connected to any related Sensei websites,
                member portal, networks, application or mobile applications, as
                well as any other services provided by us at any Sensei location
                (collectively, the “Service”).
            </Typography>

            <Typography variant="body1" paragraph>
                The Terms of Service is a legally binding contract between you
                and Sensei and must be signed before you use or receive any
                Service at any Sensei location or through any Sensei member
                portal or network.{' '}
                <b className={classes.boldText}>
                    {' '}
                    By accepting these terms, you agree that you have read and
                    understood the following terms and conditions, including the
                    Sensei Wellness Privacy Policy (“Privacy Policy
                </b>
                ,” collectively with these Terms of Service, the “
                <b className={classes.boldText}>Terms</b>”). If you are not
                eligible or do not agree to the Terms, then you do not have our
                permission to use the Service.
            </Typography>
            <Typography variant="body1" paragraph>
                1. <b className={classes.boldText}>Service Overview.</b> The
                Service is designed to provide users of the Service (“
                <b className={classes.boldText}>Members</b>,” or “
                <b className={classes.boldText}>Guests</b>”) with data-driven
                and personalized wellness, fitness, and nutrition guidance and
                services. No services offered to Guests involve medical advice,
                treatment, or diagnosis of a medical condition. In connection
                with a Guest’s participation in any Sensei Services, including a
                stay at a Sensei Retreat, information about the Guest will be
                collected by Sensei staff, through online questionnaires, online
                data input by Guest, electronic devices worn or used by the
                Guest either before, during or after Guest’s onsite Sensei
                Retreat visit, or through an equipment made available at the
                resort. As used in these Terms of Service, “
                <b className={classes.boldText}>Personal Information</b>,” “
                <b className={classes.boldText}>Sensitive Information</b>,” and
                “<b className={classes.boldText}>Aggregated Data</b>” will have
                the meanings given to them in the Privacy Policy. In providing
                personalized Services, Sensei and its staff may access and use
                the following types of Personal Information , including
                Sensitive Information, that you voluntarily provide, either
                directly or indirectly, including through the use of any
                Service, Sensei website or portal, an electronic tracking or
                monitoring device or use of an instrument or equipment, or any
                third-party website or software application connected to Sensei,
                including, but not limited to: your guest registration and
                contract information (e.g., date of birth, mailing address,
                email, phone number, dates of stay, and travel itinerary), your
                wellness and fitness goals, sleep experiences and habits,
                meditation experiences and habits, fitness experiences and
                habits, daily activities, body composition, heart rate, blood
                oxygen level, workout metrics, calories and food consumption,
                alcohol and other liquid consumption, family medical history,
                and any lifestyle, fitness, or wellness recommendations provided
                to/for you by any medical or healthcare professionals and which
                you elected to share with Sensei. Such Personal Information may
                be shared and combined with other information that you have
                previously provided to Sensei, as described in the Privacy
                Policy.
            </Typography>

            <Typography variant="body1" paragraph>
                Reports and guidance provided by Sensei, including general
                wellness and nutrition information based on your Personal
                Information, are provided solely for informational or
                educational purposes and will be sent to Guests in person, via
                text messages, emails, phone calls or through an online web
                portal. The Service, including the sending of reports and
                guidance through phone calls, text messages, emails and
                notifications within the Sensei websites, may continue after the
                Guest has left a Sensei resort. Sensei staff who may access your
                Personal Information include, but not limited to: your Sensei
                pre-arrival specialist, Sensei guide, massage therapists,
                aestheticians, fitness trainers or specialists, meditation
                specialists, nutritional specialists, chefs and meal planners,
                Sensei Retreat Director, and certain Sensei engineering and
                executive administrative staff. Your Personal Information may
                also be aggregated with data from other guests and used by
                Sensei to improve its Services. We may also use and disclose
                Aggregated Data as described in the Privacy Policy. Only Sensei
                and its authorized personnel may access and use your Personal
                Information to provide or to improve the Services, as set forth
                in the Privacy Policy.
            </Typography>
            <Typography variant="body1" paragraph>
                The Services may include spa treatments, physical movements and
                activities, physical exercises, breathing exercises, meditation,
                meals, lectures, coaching, and any recommendations thereof,
                delivered in-person or via pre-recorded or live videos or audio,
                and these Services are provided to Guests at their request for
                use or consumption at the sole discretion of the Guests and at
                their own risk. You acknowledge that there are various degrees
                of physical and emotional exertion associated with participating
                in the Services and by receiving Services offered online or at a
                Sensei Retreat you are solely responsible for determining which
                Services are suitable for you. While the Services include the
                collection of Personal Information, you understand and give
                Sensei the right to collect, store and utilize any Personal
                Information, including any Sensitive Information voluntarily
                provided by you, including through a device, instrument, or
                application used or controlled by you, to provide feedback to
                enhance the Guest’s use of the Services. You also understand it
                is your responsibility to perform physical activities
                commensurate with your level of comfort and condition and to
                promptly inform Sensei of any difficulties or conditions that
                may hinder your participation in any Services. Sensei may
                exclude any person from utilizing or participating in any
                Services. You understand that you are to consult your own
                personal physician or medical provider if you have any questions
                regarding your diet, nutrition, level of fitness or health
                condition. You hold Sensei harmless from liability attributable
                to your own fitness level or health condition, or how you decide
                to use any Service or information provided to you.
            </Typography>

            <Typography variant="body1" paragraph>
                2.{' '}
                <b className={classes.boldText}>Not a Medical Care Provider.</b>{' '}
                The Service is intended to provide data-driven guidance to
                enhance your fitness, wellness, and experience at Sensei.
                However, Sensei is not licensed to provide any medical care and
                the Service does not (and is not intended to) provide any
                medical care or medical advice, or to serve as a substitute for
                the advice of a medical professional. If you wish to receive
                medical care or medical advice, please contact a doctor or other
                licensed medical care provider. All Services, including any
                reports or guidance, provided by Sensei are provided at your
                request and for use at your sole discretion, and are intended
                solely for your personal growth and informational or educational
                purposes. If you have any medical questions regarding any
                Service, report, or guidance that you receive from Sensei,
                please consult with a healthcare professional.
            </Typography>
            <Typography variant="body1" paragraph>
                3. <b className={classes.boldText}>Medical Assistance.</b> If
                you are injured or if a health issue arises, you authorize us to
                call for medical assistance on your behalf and to arrange
                transport to a medical facility or hospital. You further agree
                to be responsible for all costs associated with any such medical
                care and related transport, and you indemnify and hold Sensei
                harmless from such costs.
            </Typography>

            <Typography variant="body1" paragraph>
                4. <b className={classes.boldText}>Eligibility.</b> You must be
                at least 18 years old to use the Service. By agreeing to these
                Terms, you represent and warrant to us that: (a) you are at
                least 18 years old; (b) you have not previously been suspended
                or removed from the Service; and (c) your registration and your
                use of the Service is in compliance with any and all applicable
                laws and regulations.
            </Typography>

            <Typography variant="body1" paragraph>
                5.{' '}
                <b className={classes.boldText}>Accounts and Registration.</b> A
                guest profile and user account will be created when you either
                sign-up on any related Sensei websites and member portals,
                networks, application or mobile applications or by making a
                booking at a Sensei Retreat, register to use the Service, or
                interact with a reservation specialist or guide. In order to
                register to use the Service, you may be required to provide us
                with some information about yourself including your name, email
                address, phone number, or other contact information. You agree
                that the information you provide to us is accurate and that you
                will keep it accurate and up to date during the period of time
                that you use the Service. The Sensei Wellness Privacy Policy
                describes how we handle your Personal Information, including
                your Sensitive Information.
            </Typography>
            <Typography variant="body1" paragraph>
                6. <b className={classes.boldText}>Limited Licenses.</b> Subject
                to your complete and ongoing compliance with these Terms, Sensei
                grants you, solely for your personal, non-commercial use, a
                limited, non-exclusive, non-transferable, non-sublicensable,
                revocable license during the term to install and use one object
                code copy of any mobile application associated with the Service
                obtained from a legitimate marketplace on a mobile device that
                you own or control, as well as access and use the Service. You
                may not: (a) reproduce, distribute, publicly display, or
                publicly perform the Service; (b) make modifications to the
                Service; or (c) interfere with or circumvent any feature of the
                Service, including any security or access control mechanism. If
                you choose to provide input and suggestions regarding problems
                with or proposed modifications or improvements to the Service (“
                <b className={classes.boldText}>Feedback</b>”), then you hereby
                grant Sensei an unrestricted, perpetual, irrevocable,
                non-exclusive, fully paid, royalty-free right to exploit the
                Feedback in any manner and for any purpose.
            </Typography>

            <Typography variant="body1" paragraph>
                7. <b className={classes.boldText}>Ownership.</b> The Service is
                owned and operated by Sensei. The visual interfaces, graphics,
                design, compilation, information, data, computer code (including
                source code or object code), products, software, services, and
                all other elements of the Service (“
                <b className={classes.boldText}>Materials.</b>”) provided by
                Sensei are protected by intellectual property and other laws.
            </Typography>

            <Typography variant="body1" paragraph>
                The Service may include or incorporate third party software
                components that are generally available free of charge under
                licenses granting recipients broad rights to copy, modify, and
                distribute those components (“
                <b className={classes.boldText}>Third Party Components</b>”).
                Although the Service is provided to you subject to these Terms,
                nothing in these Terms prevents, restricts, or is intended to
                prevent or restrict you from obtaining Third Party Components
                under the applicable third-party licenses or to limit your use
                of Third-Party Components under those third-party licenses.
            </Typography>
            <Typography variant="body1" paragraph>
                Sensei is not responsible for any third-party content, wearable
                device functionality, advice, service, or recommendations, or
                any problems or issues arising from or in connection with any
                Third Party Components, including third-party wearables,
                websites, applications, services, products, or content.
            </Typography>

            <Typography variant="body1" paragraph>
                8. <b className={classes.boldText}>Prohibited Conduct.</b> BY
                USING THE SERVICE, YOU AGREE NOT TO:
            </Typography>
            <ul>
                <li>
                    <Typography variant="body1" paragraph>
                        a. use the Service for any illegal purpose or in
                        violation of any local, state, national, or
                        international law;
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        b. access or attempt to access any information about a
                        Guest other than yourself, including their account,
                        guest profile, personal information, or other personal
                        resources or information
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        c. interfere with any security-related features of the
                        Service including disabling or circumventing features,
                        reverse engineering or otherwise attempting to discover
                        the source code of any portion of the Service except to
                        the extent that the activity is expressly permitted by
                        applicable law;
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        d. interfere with the operation of the Service or any
                        user’s enjoyment of the Service, including by: (i)
                        uploading or otherwise disseminating any virus, adware,
                        spyware, worm, or other malicious code; (ii) making any
                        unsolicited offer or advertisement to another user of
                        the Service; (iii) collecting personal information about
                        another user or third party without consent; or (iv)
                        interfering with or disrupting any network, equipment,
                        or server connected to or used to provide the Service;
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        e. perform any fraudulent activity including
                        impersonating any person or entity, claiming a false
                        affiliation, accessing any other Service account without
                        permission, or falsifying your age or date of birth or
                        any other personal information;
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        f. share your Sensei username, password, access token,
                        customized web link, or other access credential;
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        g. sell or otherwise transfer the access granted under
                        these Terms or any Materials or any right or ability to
                        view, access, or use any Materials; or
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        h. attempt to do any of the acts described in this
                        Section 8 or assist or permit any person in engaging in
                        any of the acts described in this Section 8.
                    </Typography>
                </li>
            </ul>
            <Typography variant="body1" paragraph>
                9.{' '}
                <b className={classes.boldText}>Modification of these Terms.</b>{' '}
                We reserve the right to change these Terms on a going-forward
                basis at any time upon 7 days’ notice. If a change to these
                Terms materially modifies your rights or obligations, we may
                require that you accept the modified Terms in order to continue
                to use the Service. Material modifications are effective upon
                your acceptance of the modified Terms. Immaterial modifications
                are effective upon publication. Except as expressly permitted in
                this Section 9, these Terms may be amended only by a written
                agreement signed by authorized representatives of the parties to
                these Terms.
            </Typography>
            <Typography variant="body1" paragraph>
                10.{' '}
                <b className={classes.boldText}>
                    Term, Termination and Modification of the Service.
                </b>{' '}
                These Terms are effective beginning when you accept the Terms or
                first download, install, access, or use the Service, and ending
                when terminated as described in this Section 10.
            </Typography>
            <Typography variant="body1" paragraph>
                If you violate any provision of these Terms and your
                authorization to access the Service and these Terms
                automatically terminate. Sensei may, at its sole discretion,
                terminate these Terms or your account on the Service, or suspend
                or terminate your access to the Service, at any time for any
                reason or no reason, with or without notice. You may terminate
                your account and these Terms at any time by contacting customer
                service at info@sensei.com. Upon termination of these Terms:
                your license rights will terminate, and you must immediately
                cease all use of the Service and Sections 6, 7, 10, 11, 12, 13
                and 14 will survive.
            </Typography>
            <Typography variant="body1" paragraph>
                Sensei reserves the right to modify or discontinue the Service
                at any time (including by limiting or discontinuing certain
                features of the Service), temporarily or permanently, without
                notice to you. Sensei will have no liability for any change to
                the Service or any suspension or termination of your access to
                or use of the Service.
            </Typography>
            <Typography variant="body1" paragraph>
                11. <b className={classes.boldText}>Indemnity.</b> To the
                fullest extent permitted by law, you are responsible for your
                use of the Service, and you will defend and indemnify Sensei and
                its officers, directors, employees, consultants, affiliates,
                subsidiaries and agents (together, the “
                <b className={classes.boldText}>Sensei Entities</b>”) from and
                against every claim brought by a third party, and any related
                liability, damage, loss, and expense, including reasonable
                attorneys’ fees and costs, arising out of or connected with: (a)
                your unauthorized use of, or misuse of, the Service; (b) your
                violation of any portion of these Terms, any representation,
                warranty, or agreement referenced in these Terms or any terms or
                conditions required by any third party product or service,
                including terms of use associated with any third-party device,
                application, or service that connects or shares any Sensitive
                Information with Sensei, or any applicable law or regulation; or
                (c) any dispute or issue between you and any third party. We
                reserve the right, at our own expense, to assume the exclusive
                defense and control of any matter otherwise subject to
                indemnification by you (without limiting your indemnification
                obligations with respect to that matter), and in that case, you
                agree to cooperate with our defense of those claims.
            </Typography>

            <Typography variant="body1" paragraph>
                12.{' '}
                <b className={classes.boldText}>
                    Disclaimers; No Warranties. The Service and all Materials
                    and content available through the Service are provided “as
                    is” and on an “as available” basis. All disclaimers set
                    forth herein and in the Privacy Policy, including Section 2
                    above (Not a Medical Care Provider) are hereby incorporated
                    by reference under this Section 12.
                </b>
            </Typography>
            <Typography variant="body1" paragraph>
                <b className={classes.boldText}>
                    Sensei disclaims all warranties of any kind, whether express
                    or implied, relating to the Service and all Materials and
                    content available through the Service, including: (a) any
                    implied warranty of merchantability, fitness for a
                    particular purpose, title, quiet enjoyment, or
                    non-infringement; and (b) any warranty arising out of course
                    of dealing, usage, or trade. Sensei does not warrant that
                    the Service or any portion of the Service, or any Materials
                    or content offered through the service, will be
                    uninterrupted, secure, or free of errors, viruses, or other
                    harmful components, and Sensei does not warrant that any of
                    those issues will be corrected.
                </b>
            </Typography>
            <Typography variant="body1" paragraph>
                <b className={classes.boldText}>
                    No advice or information, whether oral or written, obtained
                    by you from the Service or Sensei Entities or any Materials
                    or content available through the Service will create any
                    warranty regarding any of the Sensei Entities or the Service
                    that is not expressly stated in these Terms. We are not
                    responsible for any damage that may result from the Service
                    or your use thereof. You understand and agree that you use
                    any portion of the Service at your own discretion and risk;
                    that the Service does not provide medical advice or care;
                    and that we are not responsible for any damage or any loss
                    of data.
                </b>
            </Typography>
            <Typography variant="body1" paragraph>
                <b className={classes.boldText}>
                    The limitations, exclusions and disclaimers in this section
                    apply to the fullest extent permitted by law.
                </b>{' '}
                Sensei does not disclaim any warranty or other right that Sensei
                is prohibited from disclaiming under applicable law.
            </Typography>

            <Typography variant="body1" paragraph>
                13.{' '}
                <b className={classes.boldText}>
                    Limitations of Liability. To the fullest extent permitted by
                    law, in no event will the Sensei Entities be liable to you
                    for any indirect, incidental, special, consequential or
                    punitive damages (including damages for loss of profits,
                    goodwill, or any other intangible loss) arising out of or
                    relating to your access to or use of, or your inability to
                    access or use, the Service or any Materials or content on
                    the Service, whether based on warranty, contract, tort
                    (including negligence), statute, or any other legal theory,
                    and whether or not any Sensei Entity has been informed of
                    the possibility of damage.
                </b>
            </Typography>

            <Typography variant="body1" paragraph>
                <b className={classes.boldText}>
                    To the fullest extent permitted by law, the aggregate
                    liability of the Sensei Entities to you for all claims
                    arising out of or relating to the use of or any inability to
                    use any portion of the Service or otherwise under these
                    terms, whether in contract, tort, or otherwise, is limited
                    to the greater of: (a) the amount you have paid to Sensei
                    for access to and use of the Service in the 12 months prior
                    to the event or circumstance giving rise to claim; or (b)
                    $100.
                </b>
            </Typography>
            <Typography variant="body1" paragraph>
                <b className={classes.boldText}>
                    The allocation of risks set forth in this Section 13 is an
                    essential element of the basis of the bargain between the
                    parties. The limitations in this Section 13 will apply even
                    if any limited remedy fails of its essential purpose.
                </b>
            </Typography>

            <Typography variant="body1" paragraph>
                14. <b className={classes.boldText}>Miscellaneous</b>
            </Typography>

            <ul>
                <li>
                    <Typography variant="body1" paragraph>
                        14.1 <b className={classes.boldText}>General Terms.</b>{' '}
                        These Terms, together with the Privacy Policy and any
                        other agreements expressly incorporated by reference
                        into these Terms, are the entire and exclusive
                        understanding and agreement between you and Sensei
                        regarding your use of the Service. You may not assign or
                        transfer these Terms or your rights under these Terms,
                        in whole or in part, by operation of law or otherwise,
                        without our prior written consent. We may assign these
                        Terms at any time without notice or consent. The failure
                        to require performance of any provision will not affect
                        either party’s right to require performance at any other
                        time after that, nor will a waiver of any breach or
                        default of these Terms, or any provision of these Terms,
                        be a waiver of any subsequent breach or default or a
                        waiver of the provision itself. If any part of these
                        Terms is held to be invalid or unenforceable, the
                        unenforceable part will be given effect to the greatest
                        extent possible, and the remaining parts will remain in
                        full force and effect.
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        14.2 <b className={classes.boldText}>Governing Law.</b>{' '}
                        These Terms are governed by the laws of the State of
                        California without regard to conflict of law principles.
                        You and Sensei submit to the personal and exclusive
                        jurisdiction of the state courts and federal courts
                        located within Los Angeles, California for resolution of
                        any lawsuit or court proceeding permitted under these
                        Terms. We operate the Service from our offices in the
                        United States, and we make no representation that
                        Materials included in the Service are appropriate or
                        available for use in other locations.
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        14.3 <b className={classes.boldText}>Privacy Policy.</b>{' '}
                        You acknowledge that the personal data you provide to us
                        or that we collect in connection with the Service will
                        be used and protected as described in the Sensei Website
                        Privacy Policy, https://www.sensei.com/privacy and in
                        the Sensei Wellness Privacy Policy
                        https://sensei.com/privacy-policy-retreats/. Please read
                        these documents carefully.
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        14.4 <b className={classes.boldText}>Compliance.</b> You
                        acknowledge and agree that you are responsible for the
                        personal data and other information that you provide or
                        use in connection with the Service and are responsible
                        for complying with any applicable laws and regulations.
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        14.5{' '}
                        <b className={classes.boldText}>Additional Terms.</b>{' '}
                        Your use of the Service is subject to all additional
                        terms, policies, rules, or guidelines applicable to the
                        Service or certain features of the Service that we may
                        post on or link to from the Service (the “
                        <b className={classes.boldText}>Additional Terms</b>”).
                        All Additional Terms are incorporated by this reference
                        into, and made a part of, these Terms.
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        14.6{' '}
                        <b className={classes.boldText}>
                            Consent to Electronic Communications.
                        </b>{' '}
                        By using the Service, you consent to receiving certain
                        electronic communications from us as further described
                        in our Privacy Policy. Please read our Privacy Policy to
                        learn more about our electronic communications
                        practices.
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        14.7{' '}
                        <b className={classes.boldText}>Contact Information.</b>{' '}
                        The Service is offered by Sensei Holdings, Inc., which
                        can be reached at Sensei Holdings, Inc., 1119 Colorado
                        Ave, Suite 18, Santa Monica, CA 90401-3009. You may
                        contact us by sending correspondence to that address or
                        by emailing us at info@sensei.com.
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        14.8{' '}
                        <b className={classes.boldText}>
                            Notice to California Residents.
                        </b>{' '}
                        If you are a California resident, under California Civil
                        Code Section 1789.3, you may contact the Complaint
                        Assistance Unit of the Division of Consumer Services of
                        the California Department of Consumer Affairs in writing
                        at 1625 N. Market Blvd., Suite S-202, Sacramento,
                        California 95834, or by telephone at (800) 952-5210 in
                        order to resolve a complaint regarding the Service or to
                        receive further information regarding use of the
                        Service.
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        14.9 <b className={classes.boldText}>No Support.</b> We
                        are under no obligation to provide support for the
                        Service. In instances where we may offer support, the
                        support will be subject to published policies.
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        14.10{' '}
                        <b className={classes.boldText}>International Use.</b>{' '}
                        The Service is intended for visitors located within the
                        United States. We make no representation that the
                        Service is appropriate or available for use outside of
                        the United States. Access to the Service from countries
                        or territories or by individuals where such access is
                        illegal is prohibited
                    </Typography>
                </li>
                <li>
                    <Typography variant="body1" paragraph>
                        14.11 <b className={classes.boldText}>Arbitration.</b>{' '}
                        Any dispute arising under or in connection with these
                        Terms will be resolved only by binding arbitration
                        before one arbitrator acting under and selected in
                        accordance with the Consumer Arbitration Rules of the
                        American Arbitration Association, which are available
                        online at www.adr.org. Such arbitration wil be conducted
                        in English in Los Angeles, California, and the decision
                        of the arbitrator may be entered in any court of
                        competent jurisdiction. EACH PARTY MAY BRING CLAIMS
                        AGAINST THE OTHER PARTY ONLY IN YOUR OR ITS INDIVIDUAL
                        CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY
                        PURPORTED CLASS OR REPRESENTATIVE PROCEEDING. However,
                        the provisions of this Section 14.11 do not waive,
                        preclude, or otherwise limit the right of either party
                        to (a) bring an individual action in small claims court;
                        (b) pursue an enforcement action through an applicable
                        federal, state, or local regulatory agency; (c) seek
                        injunctive relief in a court of law in aid of such
                        arbitration; or (d) file suit in a court of law to
                        address an intellectual property infringement claim.
                    </Typography>
                </li>
            </ul>
        </div>
    );
}
